import { type CVEditingSection, type FullCV } from '@cibli/shared';
import { useMemo } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useCVTranslation } from '@/features/cv/hooks/useCVTranslation.ts';
import { markdownComponents } from '@/lib/markdown';

import type { EditableSectionSplit } from '../../../hooks/usePageDelimitation';

import EditableSection from '../../../components/form/EditableSection';
import { isNotEmpty } from '../../../utils/is-not-empty';

interface PersonalExperiencesProps {
  personalExperiences: FullCV['personalExperiences'];
  accent: string;
  onEdit?: (section: CVEditingSection) => void;
  chunk?: EditableSectionSplit;
}

export default function PersonalExperiences({
  personalExperiences,
  accent,
  onEdit,
  chunk,
}: PersonalExperiencesProps) {
  const { t, formatDate } = useCVTranslation();
  const handleEdit = () => onEdit && onEdit('personalExperiences');
  const editable = useMemo(() => onEdit !== undefined, [onEdit]);

  if (!personalExperiences?.length && !editable) return null;

  const start = chunk?.startIndex ?? 0;
  const end = chunk?.endIndex ?? personalExperiences?.length ?? 0;
  const showTitle = chunk?.showTitle ?? true;
  const items = personalExperiences?.slice(start, end) ?? [];

  return (
    <EditableSection onEdit={handleEdit} editable={editable} accentColor={accent} splitable={true}>
      <div className="rounded-lg border p-2.5">
        {showTitle && (
          <div data-splitable-header className="flex items-center gap-1 mb-4">
            <div className="h-[10px] w-[10px] rounded-sm" style={{ backgroundColor: accent }} />
            <h2 className="text-[11px] font-black uppercase tracking-widest text-black">
              {t('CV.personalExperiences')}
            </h2>
          </div>
        )}
        {!showTitle && <div className="h-3" aria-hidden />}
        <div className="space-y-5">
          {items.map((e, i) => (
            <div
              key={start + i}
              data-splitable-item
              className="break-inside-avoid [page-break-inside:avoid]"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  {isNotEmpty(e.title) && <h3 className="font-bold text-[14px]">{e.title}</h3>}
                </div>
                <div className="text-right text-black text-[12.75px]">
                  {e.startDate && e.startDate !== 'CURRENT' && (
                    <>
                      <p>
                        {e.endDate && e.endDate !== 'CURRENT'
                          ? formatDate(e.endDate, { month: 'short', year: 'numeric' })
                          : t('CV.present')}
                      </p>
                      <p>{formatDate(e.startDate, { month: 'short', year: 'numeric' })}</p>
                    </>
                  )}
                </div>
              </div>
              {isNotEmpty(e.description) && (
                <div className="mt-1 text-[13px] text-black font-semibold">
                  <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {e.description}
                  </Markdown>
                </div>
              )}
              {i !== items.length - 1 && (
                <div data-splitable-separator className="h-px bg-gray-200 mt-4" />
              )}
            </div>
          ))}
        </div>
      </div>
    </EditableSection>
  );
}
